import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useBrief } from "@/context/BriefContext";
import { track } from "@/lib/analytics";
import { Seo } from "@/components/Seo";
import { PAGE_SEO, serviceLd, breadcrumbLd, faqLd } from "@/lib/seo";
import { ArrowRight } from "lucide-react";

const fade = {
  initial: { opacity: 0, y: 16 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] },
};

const PILLARS = [
  {
    t: "Municipal innovation",
    b: "Modern services and digital infrastructure for cities and agencies, built to the workflows your teams already run.",
  },
  {
    t: "Workforce programs",
    b: "Technology and training programs that build durable capability inside your organization, not dependency on a vendor.",
  },
  {
    t: "AI readiness and governance",
    b: "Assessments, policy, and guardrails for agencies adopting AI under public accountability and real scrutiny.",
  },
  {
    t: "Procurement-ready",
    b: "Women- and minority-owned, with the documentation and structure public contracting requires.",
  },
];

const FAQS = [
  {
    q: "Are you set up for public-sector procurement?",
    a: "Yes. Copper & Vine is women- and minority-owned and procurement-ready, with the documentation and structure public contracting requires. We are built to move through agency contracting without friction.",
  },
  {
    q: "Do you work with agencies before the RFP is written?",
    a: "We prefer to. We meet you before the RFP, not after, and help you scope the work and shape the requirement so the program you procure is the one you actually need.",
  },
  {
    q: "What does AI readiness mean for a public agency?",
    a: "Assessments, policy, and guardrails for adopting AI under public accountability and real scrutiny: clear decision rights, documentation that holds up to oversight, and governance your teams can operate.",
  },
  {
    q: "How do you build capability that lasts after the engagement?",
    a: "Our workforce and technology programs build durable capability inside your organization rather than dependency on a vendor. You keep the operating record, the documentation, and a team that can run the system after we leave.",
  },
];

export default function PublicSector() {
  const { openBrief } = useBrief();

  return (
    <div className="exec-scope min-h-screen" data-testid="public-sector-page">
      <Seo
        {...PAGE_SEO.publicSector}
        jsonLd={[
          serviceLd({
            name: "Public Sector Technology Advisory",
            description: PAGE_SEO.publicSector.description,
            path: "/public-sector",
          }),
          faqLd(FAQS.map((f) => ({ q: f.q, a: f.a }))),
          breadcrumbLd([
            { name: "Home", path: "/" },
            { name: "Public sector", path: "/public-sector" },
          ]),
        ]}
      />
      <section className="exec-container pt-24 md:pt-28 pb-12">
        <motion.div {...fade} className="max-w-[820px]">
          <p className="exec-eyebrow">Public sector</p>
          <h1 className="font-extralight text-[clamp(36px,5vw,64px)] leading-[1.08] mt-6">
            Technology for the public sector.
          </h1>
          <p data-testid="public-sector-blurb" className="exec-serif text-[clamp(18px,2.1vw,24px)] leading-[1.5] text-[color:var(--lead-ink)] mt-7">
            Municipal innovation, workforce programs, and AI readiness for agencies under real
            scrutiny. Women- and minority-owned, procurement-ready. We meet you before the RFP, not
            after.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 mt-10">
            <Link
              to="/scorecard"
              data-testid="public-sector-scorecard-cta"
              onClick={() => track("door_click", { target: "/scorecard", location: "public_sector" })}
              className="exec-btn exec-btn-primary"
            >
              Take the Scorecard
            </Link>
            <button
              data-testid="public-sector-brief-cta"
              className="exec-btn exec-btn-secondary"
              onClick={() => {
                track("door_click", { target: "brief_request", location: "public_sector" });
                openBrief("general");
              }}
            >
              Request the brief
            </button>
          </div>
        </motion.div>
      </section>

      {/* Pillars */}
      <section className="exec-container pb-4">
        <div className="exec-rule" />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-12 gap-y-10 py-12">
          {PILLARS.map((p) => (
            <motion.div key={p.t} {...fade}>
              <h2 className="text-[13px] uppercase tracking-[0.2em] font-normal text-[color:var(--ink)]">{p.t}</h2>
              <p className="exec-serif text-[16.5px] leading-[1.55] text-[color:var(--lead-ink)] mt-3">{p.b}</p>
            </motion.div>
          ))}
        </div>
        <div className="exec-rule" />
      </section>

      {/* Procurement framing */}
      <section className="exec-container py-20">
        <div className="max-w-[820px]">
          <p className="exec-eyebrow">How we work with agencies</p>
          <p className="exec-serif text-[clamp(24px,3vw,36px)] leading-[1.3] text-[color:var(--ink)] mt-6">
            We are women- and minority-owned and procurement-ready, structured to move through
            public-sector contracting without friction. We help you scope the work, shape the RFP,
            and stand up programs that hold up to oversight.
          </p>
          <div className="flex flex-wrap gap-x-8 gap-y-3 mt-8">
            <Link to="/services" data-testid="public-sector-ladder-link" className="exec-link">
              See the ladder <ArrowRight size={14} />
            </Link>
            <Link to="/work" data-testid="public-sector-work-link" className="exec-link">
              See the work <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="exec-container pb-24" data-testid="public-sector-faq">
        <div className="max-w-[820px]">
          <div className="exec-rule mb-16" />
          <p className="exec-eyebrow">Common questions</p>
          <h2 className="font-light text-[clamp(28px,3.6vw,42px)] leading-[1.14] mt-4">
            What agencies ask first.
          </h2>
          <dl className="mt-8 divide-y divide-[color:var(--rule)] border-t border-[color:var(--rule)]">
            {FAQS.map((f) => (
              <motion.div key={f.q} {...fade} className="py-7" data-testid="public-sector-faq-item">
                <dt className="font-light text-[20px] sm:text-[22px] leading-[1.25]">
                  {f.q}
                </dt>
                <dd className="exec-serif text-[16.5px] leading-[1.55] text-[color:var(--lead-ink)] mt-3">{f.a}</dd>
              </motion.div>
            ))}
          </dl>
        </div>
      </section>
    </div>
  );
}
